import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, User, Briefcase, Star, BarChart2, DollarSign, LogIn, Settings, Plus } from 'lucide-react';

// Hook to detect clicks outside of a component
function useOutsideAlerter(ref: React.RefObject<HTMLElement>, callback: () => void) {
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                callback();
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [ref, callback]);
}

const Navbar = () => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const location = useLocation();

    const userMenuRef = useRef<HTMLDivElement>(null);
    const mobileMenuRef = useRef<HTMLDivElement>(null);

    useOutsideAlerter(userMenuRef, () => setIsUserMenuOpen(false));
    useOutsideAlerter(mobileMenuRef, () => setIsMobileMenuOpen(false));

    const navLinks = [
        { path: '/', label: 'Ana Sayfa' },
        { path: '/hakkimizda', label: 'Hakkımızda' },
        { path: '/blog', label: '81 İl Rehberi (Blog)' },
    ];

    const userMenuItems = [
        { path: '/my-travels', label: 'Gezilerim', icon: <Briefcase size={16} /> },
        { path: '/my-dream-list', label: 'Hayal Listem', icon: <Star size={16} /> },
        { path: '/achievements', label: 'Başarımlarım', icon: <BarChart2 size={16} /> },
        { path: '/expenditures', label: 'Harcamalarım', icon: <DollarSign size={16} /> },
        { path: '/profile', label: 'Profil', icon: <User size={16} /> },
        { path: '/settings', label: 'Ayarlar', icon: <Settings size={16} /> },
        { path: '/login', label: 'Giriş', icon: <LogIn size={16} /> },
    ];

    const isActive = (path: string) => location.pathname === path;

    // Brand Colors
    const brandBlue = 'rgb(0, 191, 255)';
    const brandDarkGray = 'rgb(65, 74, 76)';
    // const brandLightBg = 'rgb(245, 254, 253)'; // Used in Tailwind classes

    return (
        <nav className="sticky top-0 z-50 bg-[rgba(245,254,253,0.94)] backdrop-blur-md border-b border-slate-100 transition-all duration-300">
            <div className="max-w-6xl mx-auto px-4 md:px-6">
                <div className="flex items-center justify-between py-3 md:py-3.5">

                    {/* LEFT: Logo */}
                    <div className="flex items-center gap-10">
                        <Link to="/" className="text-2xl md:text-3xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500 shrink-0">
                            Say-Hot
                        </Link>

                        {/* CENTER: Nav Links (Desktop) */}
                        <div className="hidden md:flex items-center gap-6">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.path}
                                    to={link.path}
                                    className={`text-sm md:text-base font-medium transition-all duration-200 py-1 border-b-2 ${isActive(link.path)
                                            ? `text-[${brandBlue}] border-[${brandBlue}] font-semibold`
                                            : `text-[${brandDarkGray}]/80 border-transparent hover:text-[${brandBlue}] hover:border-[${brandBlue}]`
                                        }`}
                                    style={{
                                        color: isActive(link.path) ? brandBlue : undefined,
                                        borderColor: isActive(link.path) ? brandBlue : 'transparent',
                                    }}
                                >
                                    {link.label}
                                </Link>
                            ))}
                        </div>
                    </div>

                    {/* RIGHT: Actions */}
                    <div className="flex items-center gap-3 md:gap-4">

                        {/* Plan Button (Desktop) */}
                        <Link
                            to="/start-plan"
                            className="hidden md:inline-flex items-center justify-center px-5 py-2 rounded-full text-white text-sm font-semibold shadow-sm hover:shadow-md transition-all transform hover:-translate-y-0.5"
                            style={{ backgroundColor: brandBlue }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgb(0, 171, 235)'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = brandBlue}
                        >
                            Plan Oluştur
                        </Link>

                        {/* Plan Button (Mobile - Icon only) */}
                        <Link
                            to="/start-plan"
                            className="md:hidden inline-flex items-center justify-center w-9 h-9 rounded-full text-white shadow-sm hover:shadow-md transition-all"
                            style={{ backgroundColor: brandBlue }}
                        >
                            <Plus size={20} />
                        </Link>

                        {/* User Dropdown */}
                        <div ref={userMenuRef} className="relative">
                            <button
                                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                                className="w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-100 hover:border-slate-300 transition-colors focus:outline-none"
                            >
                                <User size={18} />
                            </button>

                            {isUserMenuOpen && (
                                <div className="absolute right-0 mt-3 w-64 bg-white rounded-2xl shadow-lg border border-slate-100 py-2 ring-1 ring-black ring-opacity-5 transform origin-top-right transition-all z-50">
                                    <div className="px-4 py-3 border-b border-slate-50 mb-1">
                                        <p className="text-sm font-medium text-slate-900">Hesabım</p>
                                    </div>
                                    <div className="flex flex-col gap-1 p-1">
                                        {userMenuItems.map((item) => (
                                            <Link
                                                key={item.path}
                                                to={item.path}
                                                onClick={() => setIsUserMenuOpen(false)}
                                                className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-600 rounded-xl hover:bg-slate-50 hover:text-blue-600 transition-colors"
                                            >
                                                {item.icon}
                                                {item.label}
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Mobile Menu Toggle */}
                        <div className="md:hidden flex items-center">
                            <button
                                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                                className="p-2 rounded-lg text-slate-600 hover:bg-slate-100 focus:outline-none"
                            >
                                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile Menu Overlay */}
            {isMobileMenuOpen && (
                <div ref={mobileMenuRef} className="md:hidden absolute top-full left-0 w-full bg-white border-b border-slate-100 shadow-lg py-4 px-4 flex flex-col gap-4 z-40">
                    {navLinks.map((link) => (
                        <Link
                            key={link.path}
                            to={link.path}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className={`block px-4 py-3 rounded-lg text-base font-medium ${isActive(link.path)
                                    ? 'bg-blue-50 text-blue-600'
                                    : 'text-slate-600 hover:bg-slate-50'
                                }`}
                        >
                            {link.label}
                        </Link>
                    ))}
                </div>
            )}
        </nav>
    );
};

export default Navbar;
